<template>
  <v-dialog ref="dialog" v-model="isActive" :activator="activator" width="auto" @click:outside="'persistent' in $attrs ? void 0 : onClickBackOnce()">
    <template #default="{ isActive }">
      <v-card class="px-8 pt-8 pb-6 d-flex flex-column justify-center">
        <div class="d-flex justify-space-between mb-5">
          <v-card-title class="pa-0">
            <slot name="title" />
          </v-card-title>
          <v-icon icon="mdi-window-close" color="grey-darken-2" size="28" class="mt-1" @click="onClickBackOnce" />
        </div>
        <div tabindex="0" @keyup.esc.stop="onClickBackOnce">
          <slot name="content" :is-active="isActive" />
        </div>
        <v-card-actions :class="['d-flex', removeErrorButton ? 'justify-end' : 'justify-space-between', 'pa-0', 'mt-7']" style="min-height: 36px">
          <v-btn v-if="!removeErrorButton" color="grey-darken-1" @click="onClickBackOnce">
            <strong>ANNULLA</strong>
          </v-btn>
          <v-btn :disabled="disableSuccessButton" color="success" @click="onClickConfirm">
            <strong>CONFERMA</strong>
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script setup lang="ts">
import { once } from '@/lib/functions';
import { type MaybeRefOrGetter, type WatchStopHandle, ref, toValue, watch } from 'vue';

const emit = defineEmits<{
  'click:confirm': [];
  'click:back': [];
}>();

withDefaults(defineProps<{ removeErrorButton?: boolean; activator?: 'parent' }>(), { removeErrorButton: false });

const isActive = ref(false);
const disableSuccessButton = ref(false);
let onClickBackOnce: () => void = () => void 0;

function onClickConfirm() {
  dispatchEvent(new CustomEvent('_click:confirm', { bubbles: false }));
  emit('click:confirm');
  isActive.value = false;
}

function onClickBack() {
  dispatchEvent(new CustomEvent('_click:back', { bubbles: false }));
  emit('click:back');
  isActive.value = false;
}

async function open(ctx: { disableSuccessButton?: MaybeRefOrGetter<boolean> } = {}) {
  return new Promise((res, rej) => {
    const unwatchers: WatchStopHandle[] = [];

    Object.keys(ctx).forEach((k) => {
      disableSuccessButton.value = toValue(ctx.disableSuccessButton) ?? false;

      unwatchers.push(
        watch(
          () => ctx[k as keyof typeof ctx],
          (v: any) => (disableSuccessButton.value = v),
        ),
      );
    });

    isActive.value = true;

    addEventListener(
      '_click:confirm',
      (e) => {
        e.stopPropagation();
        unwatchers.forEach((u) => u());
        res(undefined);
      },
      { once: true },
    );

    addEventListener(
      '_click:back',
      (e) => {
        e.stopPropagation();
        unwatchers.forEach((u) => u());
        rej(undefined);
      },
      { once: true },
    );
  });
}

function onEsc(e: Event) {
  if ((e as KeyboardEvent).key === 'Escape') {
    onClickBackOnce();
  }
}

watch(isActive, (isActive) => {
  if (isActive) {
    addEventListener('keyup', onEsc, { once: true });
    onClickBackOnce = once(onClickBack);
  }
});

defineExpose({ open });
</script>
